import { Code, ConnectError } from '@bufbuild/connect';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import api from '@/shared/api/api';
import { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import { ToothLandmark } from '@/shared/api/protocol-ts/model/dto_report_landmark_pb';
import {
  Tooth,
  ToothFullHeadMPR,
  Tooth_SuggestedTreatmentStatus,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { PATHS, StreamDataAccumulatorKey } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { debug, isConnectNetworkError, safeJSONParse } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { allowedToothConditionsModel } from '@/entities/allowedToothConditions';
import { assetsModel } from '@/entities/assets';
import { conditionModel, type ConditionIDGroup } from '@/entities/condition';
import { patientModel } from '@/entities/patient';
import { reportsModel } from '@/entities/reports';
import { studyModel } from '@/entities/study';
import { toothModel } from '@/entities/tooth';
import { toothLandmarksModel } from '@/entities/toothLandmarks';

type ReportDataStreamDataAccumulators = {
  [StreamDataAccumulatorKey.study]: Study[];
  [StreamDataAccumulatorKey.conditions]: Condition[];
  [StreamDataAccumulatorKey.teeth]: Tooth[];
  [StreamDataAccumulatorKey.assets]: Asset[];
  [StreamDataAccumulatorKey.toothLandmarks]: ToothLandmark[];
  [StreamDataAccumulatorKey.initialROITeethISONumbers]: number[];
  [StreamDataAccumulatorKey.initialROITeethIDs]: string[];
  [StreamDataAccumulatorKey.conditionsTeethGroup]: ConditionIDGroup;
  [StreamDataAccumulatorKey.conditionsMaxFaxGroup]: string[];
};

const createDataAccumulators = (): ReportDataStreamDataAccumulators => ({
  [StreamDataAccumulatorKey.study]: [],
  [StreamDataAccumulatorKey.conditions]: [],
  [StreamDataAccumulatorKey.teeth]: [],
  [StreamDataAccumulatorKey.assets]: [],
  [StreamDataAccumulatorKey.initialROITeethISONumbers]: [],
  [StreamDataAccumulatorKey.initialROITeethIDs]: [],
  [StreamDataAccumulatorKey.toothLandmarks]: [],
  [StreamDataAccumulatorKey.conditionsTeethGroup]: {},
  [StreamDataAccumulatorKey.conditionsMaxFaxGroup]: [],
});

export const useReportDataStream = (reportID: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const abortControllerRef = useRef<AbortController | null>(null);

  const openReportDataStream = async (silent = false) => {
    performance.mark('reportDataStream:start');

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const dataAccumulators = createDataAccumulators();
    const requestID = crypto.randomUUID();
    const fullHeadMprRecords: ToothFullHeadMPR[] = [];
    const fullHeadMprSnapshot = { completed: false };

    dispatch(reportsModel.actions.startDataStream(reportID));
    dispatch(
      reportsModel.actions.startFullHeadMprSnapshot({ reportID, requestID }),
    );

    // NOTE: This stream owns reportsModel.loading (it writes both 'succeeded' and
    // 'failed'), so a fresh open has to reset it. Otherwise a 'failed' left by the
    // previously opened report leaks into this one and releases the loading gates
    // before its data arrives. A silent reconnect keeps the current value so a
    // network blip does not throw an already rendered report back to a spinner.
    if (!silent) {
      dispatch(reportsModel.actions.setLoading('pending'));
    }

    dispatch(assetsModel.actions.setLoading('pending'));
    dispatch(toothModel.actions.setLoading('pending'));
    dispatch(conditionModel.actions.setLoading('pending'));
    dispatch(toothLandmarksModel.actions.setLoading('pending'));
    dispatch(assetsModel.actions.startReportAssetsSnapshot({ reportID }));
    dispatch(
      conditionModel.actions.startReportConditionsSnapshot({ reportID }),
    );

    try {
      const reportDataStream = api.report.reportDataStream(
        { ReportID: reportID },
        { signal: abortController.signal },
      );

      for await (const { Update } of reportDataStream) {
        if (abortController.signal.aborted) {
          return;
        }
        switch (Update.case) {
          case 'HistoricalToothFullHeadMPR': {
            fullHeadMprRecords.push(Update.value);
            break;
          }
          case 'EndOfHistoricalToothFullHeadMPR': {
            fullHeadMprSnapshot.completed = true;
            dispatch(
              reportsModel.actions.completeFullHeadMprSnapshot({
                requestID,
                records: fullHeadMprRecords,
              }),
            );
            break;
          }
          case 'HistoricalReport': {
            dispatch(reportsModel.actions.setNewestOne(Update.value));
            dispatch(reportsModel.actions.setCurrentReport(Update.value));
            dispatch(reportsModel.actions.setLoading('succeeded'));
            break;
          }
          case 'HistoricalPatient': {
            // NOTE: reportDataStream carries the report's own patient, so pages that
            // only need patient identity (preview/print) don't have to wait on the
            // much wider patientProfileStream (study/sharing/albums/org staff) that
            // usePatientProfileStream opens for the interactive workspace.
            dispatch(patientModel.actions.addOne(Update.value));
            dispatch(patientModel.actions.setCurrentPatient(Update.value));
            dispatch(patientModel.actions.setLoading('succeeded'));
            break;
          }
          case 'HistoricalStudy': {
            dataAccumulators[StreamDataAccumulatorKey.study].push(Update.value);
            break;
          }
          case 'EndOfHistoricalStudies': {
            dispatch(
              studyModel.actions.setNewestMany(
                dataAccumulators[StreamDataAccumulatorKey.study],
              ),
            );
            dispatch(studyModel.actions.setLoading('succeeded'));
            dataAccumulators[StreamDataAccumulatorKey.study] = [];
            break;
          }
          case 'HistoricalTooth': {
            dataAccumulators[StreamDataAccumulatorKey.teeth].push(Update.value);

            // Collect initial local ROI teeth IDs
            if (Update.value.IsInROI) {
              if (Update.value.Numeration?.ISO) {
                dataAccumulators[
                  StreamDataAccumulatorKey.initialROITeethISONumbers
                ].push(Update.value.Numeration.ISO);
              }

              dataAccumulators[
                StreamDataAccumulatorKey.initialROITeethIDs
              ].push(Update.value.ID);
            }
            break;
          }
          case 'ConditionCodesMap': {
            dispatch(
              allowedToothConditionsModel.actions.setConditionCodesMap(
                Update.value,
              ),
            );
            break;
          }
          case 'EndOfHistoricalTeeth': {
            dispatch(
              toothModel.actions.setMany(
                dataAccumulators[StreamDataAccumulatorKey.teeth],
              ),
            );

            // TODO: Deprecated. Get rif of.
            dispatch(
              toothModel.actions.setLocalROITeethISONumbers_DEPRECATED(
                dataAccumulators[
                  StreamDataAccumulatorKey.initialROITeethISONumbers
                ],
              ),
            );

            dispatch(
              toothModel.actions.initLocalROITeethIDs(
                dataAccumulators[StreamDataAccumulatorKey.initialROITeethIDs],
              ),
            );

            // Initialize user saved comment tooth IDs
            const userSavedCommentToothIDs = dataAccumulators[
              StreamDataAccumulatorKey.teeth
            ]
              .filter((tooth) => {
                // Comment exists and it's not an AI-generated suggested treatment
                return (
                  tooth.Comment &&
                  tooth.SuggestedTreatment?.Status !==
                    Tooth_SuggestedTreatmentStatus.DONE
                );
              })
              .map((tooth) => tooth.ID);

            dispatch(
              toothModel.actions.initializeUserSavedCommentToothIDs(
                userSavedCommentToothIDs,
              ),
            );

            dispatch(toothModel.actions.setLoading('succeeded'));

            dataAccumulators[
              StreamDataAccumulatorKey.initialROITeethISONumbers
            ] = [];
            dataAccumulators[StreamDataAccumulatorKey.initialROITeethIDs] = [];
            dataAccumulators[StreamDataAccumulatorKey.teeth] = [];

            break;
          }
          case 'HistoricalCondition': {
            const conditionsTeethGroup =
              dataAccumulators[StreamDataAccumulatorKey.conditionsTeethGroup];
            dataAccumulators[StreamDataAccumulatorKey.conditions].push(
              Update.value,
            );

            const condition = Update.value;
            const toothID =
              condition.Attribution.case === 'Tooth'
                ? condition.Attribution.value.ToothID
                : '';

            if (condition.Attribution.case === 'Tooth') {
              if (toothID in conditionsTeethGroup) {
                conditionsTeethGroup[toothID].push(condition.ID);
              } else {
                conditionsTeethGroup[toothID] = [condition.ID];
              }
            } else {
              dataAccumulators[
                StreamDataAccumulatorKey.conditionsMaxFaxGroup
              ].push(condition.ID);
            }

            break;
          }
          case 'EndOfHistoricalConditions': {
            dispatch(
              conditionModel.actions.setMany(
                dataAccumulators[StreamDataAccumulatorKey.conditions],
              ),
            );
            dispatch(
              conditionModel.actions.initializeTeethGroup(
                dataAccumulators[StreamDataAccumulatorKey.conditionsTeethGroup],
              ),
            );
            dispatch(
              conditionModel.actions.initializeMaxFaxGroup(
                dataAccumulators[
                  StreamDataAccumulatorKey.conditionsMaxFaxGroup
                ],
              ),
            );

            dispatch(conditionModel.actions.setLoading('succeeded'));
            dispatch(
              conditionModel.actions.completeReportConditionsSnapshot({
                reportID,
              }),
            );

            // Clear accumulators
            dataAccumulators[StreamDataAccumulatorKey.conditions] = [];
            dataAccumulators[StreamDataAccumulatorKey.conditionsTeethGroup] =
              {};
            dataAccumulators[StreamDataAccumulatorKey.conditionsMaxFaxGroup] =
              [];

            break;
          }
          case 'HistoricalAsset': {
            // NOTE: We don't need deleted assets in store
            if (!Update.value.Deleted?.Deleted) {
              dataAccumulators[StreamDataAccumulatorKey.assets].push(
                Update.value,
              );
            }
            break;
          }
          case 'EndOfHistoricalAssets': {
            dispatch(
              assetsModel.actions.addMany(
                dataAccumulators[StreamDataAccumulatorKey.assets],
              ),
            );
            dispatch(assetsModel.actions.setLoading('succeeded'));
            dispatch(
              assetsModel.actions.completeReportAssetsSnapshot({ reportID }),
            );
            dataAccumulators[StreamDataAccumulatorKey.assets] = [];
            break;
          }
          case 'HistoricalToothLandmark': {
            dataAccumulators[StreamDataAccumulatorKey.toothLandmarks].push(
              Update.value,
            );
            break;
          }
          case 'EndOfHistoricalTeethLandmarks': {
            dispatch(
              toothLandmarksModel.actions.addMany(
                dataAccumulators[StreamDataAccumulatorKey.toothLandmarks],
              ),
            );
            dispatch(toothLandmarksModel.actions.setLoading('succeeded'));

            dataAccumulators[StreamDataAccumulatorKey.toothLandmarks] = [];
            break;
          }
          default: {
            performance.mark('reportDataStream:end');
            performance.measure(
              'reportDataStream',
              'reportDataStream:start',
              'reportDataStream:end',
            );
          }
        }
      }

      if (!abortController.signal.aborted) {
        dispatch(reportsModel.actions.setDataStreamSucceeded(reportID));
      }
      if (!abortController.signal.aborted && !fullHeadMprSnapshot.completed) {
        dispatch(reportsModel.actions.failFullHeadMprSnapshot(requestID));
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      const connectError = ConnectError.from(error);
      if (connectError.code === Code.Unauthenticated) {
        navigate(PATHS.signIn, {
          state: { from: location?.pathname + location?.search },
        });
      }
      const entity = safeJSONParse(connectError.rawMessage)?.entity;

      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closeReportDataStream(false);
        void openReportDataStream(true);
        return;
      }

      // NOTE: Redirect only if error entity is report or the user doesn't have access to the report. In other cases it's not about report itself.
      if (
        (connectError.code === Code.NotFound && entity === 'report') ||
        connectError.code === Code.PermissionDenied
      ) {
        toastCaller({
          type: 'error',
          message: formatMessage({
            id: 'reportStream.notExistOrNoAccess',
            defaultMessage:
              'The page does not exist, or you do not have permission to access this page',
          }),
          autoClose: 10000,
        });

        navigate(PATHS.patients);
      }

      // NOTE: A stream that died before HistoricalReport would otherwise leave
      // reportsModel.loading at 'pending' forever, hanging every loading gate.
      dispatch(reportsModel.actions.setLoading('failed'));
      dispatch(reportsModel.actions.setDataStreamFailed(reportID));
      dispatch(reportsModel.actions.failFullHeadMprSnapshot(requestID));
      dispatch(assetsModel.actions.failReportAssetsSnapshot({ reportID }));
      dispatch(
        conditionModel.actions.failReportConditionsSnapshot({ reportID }),
      );
    }
  };

  const closeReportDataStream = (resetStore = true) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (resetStore) {
      dispatch(reportsModel.actions.resetDataStream());
      dispatch(reportsModel.actions.resetReportViewSettings());
      dispatch(assetsModel.actions.removeAllButNoGuarded());
      dispatch(assetsModel.actions.resetReportAssetsSnapshot({ reportID }));
      dispatch(toothModel.actions.initLocalROITeethIDs([]));
      dispatch(conditionModel.actions.reset());
      dispatch(toothLandmarksModel.actions.reset());
      dispatch(allowedToothConditionsModel.actions.reset());
    }
  };

  useEffect(() => {
    void openReportDataStream();

    return () => {
      closeReportDataStream();
    };
  }, [reportID]);
};
